import type { NewPlateItem } from "../lib/plate";
import type { LoggingFlowReviewItem } from "../lib/logging-flow";
import { OffUnavailableError, OffValidationError } from "../lib/openfoodfacts-client";

// Barcode flow pure helpers (plan todo 16). This module is the unit-testable
// seam: the repo has no jsdom, so scanner interactivity (serving switching,
// quantity stepping, barcode validation, error classification) is asserted
// through these pure functions. No zxing imports live here — the camera
// lifecycle is isolated in barcode-scanner-session.ts.

export interface NutritionValues {
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
}

// Structural mirror of the MappedOffProduct shape from the browser-direct
// client (src/lib/openfoodfacts-client.ts). Kept local so the screen and its
// tests stay decoupled from the fetch layer.
export interface OffProduct {
  readonly barcode?: string;
  readonly name?: string;
  readonly brands?: string;
  readonly servingSize?: string;
  readonly imageUrl?: string;
  readonly imageFrontUrl?: string;
  readonly per100g: NutritionValues;
  readonly perServing: NutritionValues;
  readonly incomplete: boolean;
  readonly noNutritionData: boolean;
  readonly hasBarcode: boolean;
}

// Manual barcode entry: EAN-8/UPC-E through 14-digit codes (the OFF client
// validates the same range before any network call).
export const MIN_BARCODE_DIGITS = 8;
export const MAX_BARCODE_DIGITS = 14;
const BARCODE_INPUT_PATTERN = /^\d{8,14}$/;

export function normalizeBarcodeInput(input: string): string {
  return input.trim();
}

export function isValidBarcodeInput(input: string): boolean {
  return BARCODE_INPUT_PATTERN.test(normalizeBarcodeInput(input));
}

// Serving selector: "1 serving (serving_size)" exists only when OFF reports a
// serving size; "100 g" is always available.
export type ServingMode = "serving" | "per100g";

export function defaultServingMode(product: OffProduct): ServingMode {
  return product.servingSize === undefined ? "per100g" : "serving";
}

export function availableServingModes(product: OffProduct): readonly ServingMode[] {
  return product.servingSize === undefined ? ["per100g"] : ["serving", "per100g"];
}

export function servingModeLabel(product: OffProduct, mode: ServingMode): string {
  if (mode === "serving") {
    return `1 serving (${product.servingSize ?? "serving"})`;
  }
  return "100 g";
}

// Quantity stepper: quantity counts base units — servings in "serving" mode,
// 100 g units in "per100g" mode.
export const QUANTITY_STEP = 0.5;
export const MIN_QUANTITY = 0.5;
export const MAX_QUANTITY = 20;

export function clampQuantity(quantity: number): number {
  if (!Number.isFinite(quantity)) return 1;
  return Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, quantity));
}

export function stepQuantity(quantity: number, direction: 1 | -1): number {
  // Round to one decimal to avoid float drift (0.5 steps).
  return clampQuantity(Math.round((quantity + direction * QUANTITY_STEP) * 10) / 10);
}

export function quantityLabel(mode: ServingMode, quantity: number): string {
  if (mode === "serving") {
    return quantity === 1 ? "1 serving" : `${quantity} servings`;
  }
  return `${Math.round(quantity * 100)} g`;
}

// Scales ONLY defined nutrients; undefined stays undefined — missing
// nutrition is never treated as 0 anywhere in the flow.
export function scaleNutrition(values: NutritionValues, factor: number): NutritionValues {
  const scale = (value: number | undefined): number | undefined =>
    value === undefined ? undefined : Math.round(value * factor * 10) / 10;
  return {
    ...(values.calories === undefined ? {} : { calories: scale(values.calories) }),
    ...(values.proteinG === undefined ? {} : { proteinG: scale(values.proteinG) }),
    ...(values.carbsG === undefined ? {} : { carbsG: scale(values.carbsG) }),
    ...(values.fatG === undefined ? {} : { fatG: scale(values.fatG) }),
    ...(values.fiberG === undefined ? {} : { fiberG: scale(values.fiberG) }),
  };
}

// Nutrition for the current selection: per-serving values x servings, or
// per-100g values x (grams / 100).
export function productNutrition(
  product: OffProduct,
  mode: ServingMode,
  quantity: number,
): NutritionValues {
  const base = mode === "serving" ? product.perServing : product.per100g;
  return scaleNutrition(base, quantity);
}

export function productDisplayName(product: OffProduct): string {
  const name = product.name?.trim();
  return name === undefined || name === "" ? "Unknown product" : name;
}

export function showIncompleteBadge(product: OffProduct): boolean {
  return product.incomplete || product.noNutritionData;
}

// Plate mapping (todo-11 contract): quantity/unit follow the serving mode so
// the plate row reads "1.5 serving" or "250 g". dataSource is always
// "barcode"; sourceRef points at the OFF barcode.
export function plateItemFromProduct(
  product: OffProduct,
  barcode: string,
  mode: ServingMode,
  quantity: number,
): NewPlateItem {
  const nutrition = productNutrition(product, mode, quantity);
  return {
    name: productDisplayName(product),
    quantity: mode === "serving" ? quantity : Math.round(quantity * 100),
    unit: mode === "serving" ? "serving" : "g",
    ...nutrition,
    dataSource: "barcode",
    sourceRef: { kind: "off", id: barcode },
  };
}

// Review mapping (todo-12 contract): identical nutrition, without the
// plate-local sourceRef.
export function reviewItemFromProduct(
  product: OffProduct,
  mode: ServingMode,
  quantity: number,
): LoggingFlowReviewItem {
  const nutrition = productNutrition(product, mode, quantity);
  return {
    name: productDisplayName(product),
    quantity: mode === "serving" ? quantity : Math.round(quantity * 100),
    unit: mode === "serving" ? "serving" : "g",
    ...nutrition,
    dataSource: "barcode",
  };
}

// "Save as frequent food" (api.customFoods.create): stores per-serving
// nutrition when a serving size exists, otherwise per-100g with a "100 g"
// serving unit. Undefined nutrients stay omitted (never 0).
export interface CustomFoodArgs {
  readonly name: string;
  readonly brand?: string;
  readonly servingUnit: string;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly barcode: string;
  readonly source: "barcode";
}

export function customFoodArgsFromProduct(product: OffProduct, barcode: string): CustomFoodArgs {
  const hasServing = product.servingSize !== undefined;
  const base = hasServing ? product.perServing : product.per100g;
  const brand = product.brands?.trim();
  return {
    name: productDisplayName(product),
    ...(brand === undefined || brand === "" ? {} : { brand }),
    servingUnit: hasServing ? (product.servingSize as string) : "100 g",
    ...base,
    barcode,
    source: "barcode",
  };
}

// OFF lookup failures: the browser-direct client (src/lib/openfoodfacts-client.ts)
// throws typed OffUnavailableError / OffValidationError; the message checks
// below keep older ConvexError-shaped strings ("... OFF_UNAVAILABLE")
// classifying correctly.
export type LookupErrorKind = "unavailable" | "validation" | "unknown";

export function classifyLookupError(error: unknown): LookupErrorKind {
  if (error instanceof OffUnavailableError) return "unavailable";
  if (error instanceof OffValidationError) return "validation";
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes("OFF_UNAVAILABLE")) return "unavailable";
  if (message.includes("VALIDATION")) return "validation";
  return "unknown";
}

// Camera start failures (getUserMedia DOMException names) drive the guidance
// copy on the manual-entry fallback state.
export type CameraErrorKind = "denied" | "unavailable" | "unknown";

export function classifyCameraError(error: unknown): CameraErrorKind {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "NotReadableError" || name === "OverconstrainedError") {
    return "unavailable";
  }
  return "unknown";
}
